import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SuperAdminLayout from './SuperAdminLayout';
import SuperAdminService, { Company } from '../../services/SuperAdminService';
import {
  PosErrorCause,
  PosErrorRange,
  PosErrorResult,
  PosErrorRow,
  PosErrorSummary,
  getPosError,
  getPosErrorCauses,
  getPosErrorSummary,
  getPosErrors,
  reviewPosError,
} from '../../services/PosErrorAdminService';

// ============================================================================
// SÜPER ADMİN › KASA HATALARI
// Kasada "Ödeme Kaydı Hatası" ekranı her çıktığında bir satır oluşur. Sunucu, satışın
// gerçekten kaydedilip kaydedilmediğini işlem anahtarıyla kendisi kontrol eder.
// Kırmızı satırlar (kayıp şüphesi): yazarkasa parayı aldı ama satış GTS'de yok.
// ============================================================================

const REFRESH_MS = 30000;
const PAGE_SIZE = 25;
const TZ = 'Europe/Istanbul';

type PageTab = 'lost' | 'all' | 'causes';

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.25, 2),
  textAlign: 'center',
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  height: '100%',
}));

const RANGE_OPTIONS: { value: PosErrorRange; label: string; causesTitle: string; statPrefix: string }[] = [
  { value: 'today', label: 'Bugün', causesTitle: 'Bugün en sık görülen sebepler', statPrefix: 'Bugün kasada' },
  { value: 'yesterday', label: 'Dün', causesTitle: 'Dün en sık görülen sebepler', statPrefix: 'Dün kasada' },
  { value: '7d', label: 'Son 7 gün', causesTitle: 'Son 7 günde en sık görülen sebepler', statPrefix: 'Son 7 günde kasada' },
  { value: '30d', label: 'Son 30 gün', causesTitle: 'Son 30 günde en sık görülen sebepler', statPrefix: 'Son 30 günde kasada' },
];

const FISCAL_DEVICE_LABEL: Record<string, string> = {
  pavo_rest_p2p: 'Pavo Rest-P2P',
  pavo_p2p: 'Pavo sipariş modu',
  pavopay: 'Pavo REST',
  beko: 'Beko',
  ingenico: 'Ingenico',
  inpos: 'Inpos',
  hugin: 'Hugin',
  yok: 'Yazarkasasız',
};

const ERROR_KIND_LABEL: Record<string, string> = {
  'zaman-asimi': 'Zaman aşımı',
  ag: 'Bağlantı koptu',
  sunucu: 'Sunucu reddetti',
  istemci: 'Tarayıcı hatası',
};

const SCREEN_LABEL: Record<string, string> = {
  restoran: 'Restoran',
  'hizli-satis': 'Hızlı satış',
  paket: 'Paket',
};

const RESULT_PILL: Record<PosErrorResult, { label: string; bg: string; fg: string }> = {
  'kayip-suphesi': { label: 'Kayıp şüphesi', bg: '#fdecea', fg: '#b71c1c' },
  'sunucuda-kayitli': { label: 'Sunucuda kayıtlı', bg: '#e3f2fd', fg: '#0d47a1' },
  'yeniden-denemede-kaydedildi': { label: 'Yeniden denemede kaydedildi', bg: '#e8f5e9', fg: '#1b5e20' },
  'para-alinmadi': { label: 'Para alınmadı', bg: '#eceff1', fg: '#37474f' },
  'kontrol-ediliyor': { label: 'Kontrol ediliyor', bg: '#fff3e0', fg: '#8a4b00' },
  bilinmiyor: { label: 'Bilinmiyor', bg: '#eceff1', fg: '#37474f' },
};

const pillOf = (r: PosErrorRow) => RESULT_PILL[r.result ?? 'bilinmiyor'] ?? RESULT_PILL.bilinmiyor;

const isPavo = (d?: string | null) => d === 'pavo_rest_p2p' || d === 'pavo_p2p' || d === 'pavopay';

const deviceLabel = (d?: string | null) => (d ? FISCAL_DEVICE_LABEL[d] ?? d : '');

/** Ödeme türü backend'den serbest metin gelebilir; bilinen adlara eşlenir. */
const paymentKindLabel = (k?: string | null) => {
  if (!k) return '';
  const n = k
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/[\s_]+/g, '-');
  if (n === 'nakit' || n === 'cash') return 'Nakit';
  if (n === 'kart' || n === 'card' || n === 'kredi-karti' || n === 'credit-card') return 'Kart';
  if (n === 'karma' || n === 'mixed' || n === 'split' || n === 'parcali') return 'Karma';
  if (n === 'acik-hesap' || n === 'acikhesap' || n === 'cari' || n === 'open-account' || n === 'openaccount') return 'Açık hesap';
  if (n === 'diger' || n === 'diger-odeme' || n === 'other') return 'Diğer ödeme';
  if (n === 'online') return 'Online';
  return k;
};

/** "Pavo Rest-P2P · Kart" — yazarkasasız ödemede yalnız ödeme türü. */
const paymentPath = (r: PosErrorRow) => {
  const kind = paymentKindLabel(r.paymentKind);
  const dev = r.fiscalDevice && r.fiscalDevice !== 'yok' ? deviceLabel(r.fiscalDevice) : '';
  return [dev, kind].filter(Boolean).join(' · ') || '—';
};

const fiscalRefLabel = (r: PosErrorRow) => {
  if (!r.fiscalReference) return '';
  if (isPavo(r.fiscalDevice)) return `Pavo satış no ${r.fiscalReference}`;
  const dev = deviceLabel(r.fiscalDevice);
  return dev && r.fiscalDevice !== 'yok' ? `${dev} fiş no ${r.fiscalReference}` : `Fiş no ${r.fiscalReference}`;
};

const fmtMoney = (amount?: number | null) =>
  amount == null
    ? '—'
    : `₺ ${amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const toDate = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const partsTR = (d: Date) => {
  const parts = new Intl.DateTimeFormat('tr-TR', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { day: get('day'), month: get('month'), year: get('year'), hour: get('hour'), minute: get('minute'), second: get('second') };
};

/** Türkiye saati "dd.MM HH:mm:ss" */
const fmtStamp = (d: Date | null) => {
  if (!d) return '—';
  const p = partsTR(d);
  return `${p.day}.${p.month} ${p.hour}:${p.minute}:${p.second}`;
};

/** Türkiye saati "HH:mm:ss" */
const fmtClock = (d: Date | null) => {
  if (!d) return '—';
  const p = partsTR(d);
  return `${p.hour}:${p.minute}:${p.second}`;
};

const fmtDuration = (ms?: number | null) => {
  if (ms == null) return '';
  if (ms < 10000) {
    const s = Math.round(ms / 100) / 10;
    return `${s.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} sn`;
  }
  if (ms < 120000) return `${Math.round(ms / 1000)} sn`;
  return `${Math.round(ms / 60000)} dk`;
};

const shortKey = (v?: string | null) => {
  if (!v) return '—';
  return v.length > 12 ? `${v.slice(0, 4)}…${v.slice(-4)}` : v;
};

/** İnceleyen kişi: ad gösterilir, e-posta asla. */
const personName = (v?: string | null) => {
  if (!v) return '';
  return v.includes('@') ? 'Yönetici' : v;
};

const parseBrowser = (ua?: string | null) => {
  if (!ua) return '';
  const pick = (re: RegExp, name: string) => {
    const m = ua.match(re);
    return m ? `${name} ${m[1]}` : '';
  };
  return (
    pick(/Edg(?:e|A|iOS)?\/(\d+)/, 'Edge') ||
    pick(/OPR\/(\d+)/, 'Opera') ||
    pick(/YaBrowser\/(\d+)/, 'Yandex') ||
    pick(/SamsungBrowser\/(\d+)/, 'Samsung Internet') ||
    pick(/Firefox\/(\d+)/, 'Firefox') ||
    pick(/(?:Chrome|CriOS)\/(\d+)/, 'Chrome') ||
    pick(/Version\/(\d+)[^ ]* (?:Mobile\/\S+ )?Safari/, 'Safari') ||
    'Bilinmeyen tarayıcı'
  );
};

const browserAndVersion = (r: PosErrorRow) => {
  const b = parseBrowser(r.userAgent);
  const v = r.appVersion ? `web v${r.appVersion.replace(/^v/i, '')}` : '';
  return [b, v].filter(Boolean).join(' · ') || '—';
};

const endpointName = (e?: string | null) => {
  if (!e) return '—';
  const clean = e.split('?')[0].replace(/\/+$/, '');
  return clean.slice(clean.lastIndexOf('/') + 1) || clean;
};

/** Sonuç pilinin altındaki kısa not. */
const resultNote = (r: PosErrorRow) => {
  switch (r.result) {
    case 'kayip-suphesi':
      return fiscalRefLabel(r) || 'Yazarkasa ödemeyi aldı';
    case 'sunucuda-kayitli':
      return r.serverReference || '';
    case 'yeniden-denemede-kaydedildi':
      return r.attempt != null ? `${r.attempt + 1}. denemede` : r.serverReference || '';
    case 'para-alinmadi':
      if (!r.fiscalDevice || r.fiscalDevice === 'yok') return 'Yazarkasa çağrılmadı';
      return r.fiscalDone === false ? 'Yazarkasa ödemeyi almadı' : 'Yazarkasa çağrılmadı';
    case 'kontrol-ediliyor':
      return 'Sunucu kontrolü bekleniyor';
    default:
      return r.serverCheck === 3 ? 'Sunucu kontrol edilemedi' : '';
  }
};

const errorKindText = (r: PosErrorRow) => {
  const kind = r.errorKind ? ERROR_KIND_LABEL[r.errorKind] ?? r.errorKind : 'Hata';
  return r.httpStatus && r.errorKind === 'sunucu' ? `${kind} (HTTP ${r.httpStatus})` : kind;
};

const screenText = (r: PosErrorRow) => {
  const s = r.screenMode ? SCREEN_LABEL[r.screenMode] ?? r.screenMode : '';
  return [s, r.tableName ? (/^masa/i.test(r.tableName) ? r.tableName : `Masa ${r.tableName}`) : ''].filter(Boolean).join(' · ');
};

const tableTitle = (r: PosErrorRow) => {
  if (!r.tableName) return '';
  return /^masa/i.test(r.tableName) ? r.tableName : `Masa ${r.tableName}`;
};

// ---------------------------------------------------------------------------
// Detay: sunucu kontrol kutusu
// ---------------------------------------------------------------------------
const serverBox = (r: PosErrorRow): { bg: string; fg: string; title: string; text: string } => {
  const dev = deviceLabel(r.fiscalDevice);
  const kind = paymentKindLabel(r.paymentKind).toLocaleLowerCase('tr-TR');
  switch (r.result) {
    case 'kayip-suphesi': {
      const ref = r.fiscalReference ? (isPavo(r.fiscalDevice) ? `satış no ${r.fiscalReference}` : `fiş no ${r.fiscalReference}`) : '';
      const inner = [ref, kind].filter(Boolean).join(', ');
      const who = dev && r.fiscalDevice !== 'yok' ? `${isPavo(r.fiscalDevice) ? 'Pavo' : dev} cihazı` : 'Yazarkasa';
      return {
        bg: '#fdecea',
        fg: '#8a1c1c',
        title: 'Sunucu kontrolü: bu ödemeye ait satış bulunamadı',
        text: `${who} ödemeyi aldı${inner ? ` (${inner})` : ''}, ama bu işlem anahtarıyla GTS'de satış kaydı yok. Kasada ${
          r.tableName ? 'bu masanın' : 'bu satışın'
        } durumunu kontrol edin; gerekirse satış elle girilmeli.`,
      };
    }
    case 'sunucuda-kayitli':
      return {
        bg: '#e3f2fd',
        fg: '#0d47a1',
        title: 'Sunucu kontrolü: satış sunucuda kayıtlı',
        text: `Kasada hata göründü ama satış GTS'ye kaydedilmiş${
          r.serverReference ? ` (${r.serverReference})` : ''
        }. Yanlış alarm; satışın yeniden girilmesi gerekmez.`,
      };
    case 'yeniden-denemede-kaydedildi':
      return {
        bg: '#e8f5e9',
        fg: '#1b5e20',
        title: 'Satış yeniden denemede kaydedildi',
        text: `İlk kayıt isteği hata verdi, kasa yeniden denedi ve satış kaydedildi${
          r.serverReference ? ` (${r.serverReference})` : ''
        }.`,
      };
    case 'para-alinmadi':
      return {
        bg: '#eceff1',
        fg: '#37474f',
        title: 'Para alınmadı',
        text:
          r.fiscalDevice && r.fiscalDevice !== 'yok'
            ? 'Yazarkasa ödemeyi almadan kayıt hatası oluştu ve satış kaydı da yok. Para kaybı beklenmez; ödeme kasada yeniden alınabilir.'
            : 'Yazarkasa çağrılmadan kayıt hatası oluştu ve satış kaydı da yok. Ödeme kasada yeniden alınabilir.',
      };
    case 'kontrol-ediliyor':
      return {
        bg: '#fff3e0',
        fg: '#8a4b00',
        title: 'Sunucu kontrolü bekleniyor',
        text: 'Bu hata için sunucu kontrolü henüz tamamlanmadı. Birazdan yenileyin.',
      };
    default:
      return {
        bg: '#eceff1',
        fg: '#37474f',
        title: 'Sunucu kontrolü yapılamadı',
        text: 'Kayıtta işlem anahtarı yok ya da kontrol sırasında hata oluştu. Satışın durumunu kasada doğrulayın.',
      };
  }
};

// ---------------------------------------------------------------------------
// Detay: "Kasada olanlar" — yalnız kayıttaki alanlardan türetilir, uydurma adım yok.
// ---------------------------------------------------------------------------
type Step = { time: string; dot: string; fg: string; text: string };
const DOT = { ok: '#2e7d32', bad: '#c62828', mid: '#1976d2', grey: '#90a4ae' };

const buildSteps = (r: PosErrorRow): Step[] => {
  const steps: Step[] = [];
  const occurred = toDate(r.occurredAt);
  const sent = occurred && r.durationMs != null ? new Date(occurred.getTime() - r.durationMs) : null;
  const dev = deviceLabel(r.fiscalDevice);
  const devShort = isPavo(r.fiscalDevice) ? 'Pavo' : dev;
  const kind = paymentKindLabel(r.paymentKind).toLocaleLowerCase('tr-TR');

  if (r.fiscalDevice && r.fiscalDevice !== 'yok' && r.fiscalDone != null) {
    if (r.fiscalDone) {
      const ref = r.fiscalReference ? (isPavo(r.fiscalDevice) ? `satış no ${r.fiscalReference}` : `fiş no ${r.fiscalReference}`) : '';
      const inner = [kind, ref].filter(Boolean).join(', ');
      steps.push({
        time: '—',
        dot: DOT.ok,
        fg: '#1f2933',
        text: `${devShort} ödemeyi aldı${inner ? `: ${inner}` : ''} (${fmtMoney(r.amount)}).`,
      });
    } else {
      steps.push({ time: '—', dot: DOT.grey, fg: '#52606d', text: `${devShort} ödemeyi almadı.` });
    }
  }

  steps.push({
    time: fmtClock(sent),
    dot: DOT.mid,
    fg: '#1f2933',
    text: `Satış kaydı sunucuya gönderildi${r.attempt && r.attempt > 1 ? ` (${r.attempt}. deneme)` : ''}.`,
  });

  const dur = fmtDuration(r.durationMs);
  let errText: string;
  switch (r.errorKind) {
    case 'zaman-asimi':
      errText = `Tarayıcı ${dur || 'süre dolana kadar'} bekledi, cevap gelmedi`;
      break;
    case 'ag':
      errText = `Bağlantı koptu${dur ? ` (${dur} sonra)` : ''}`;
      break;
    case 'sunucu':
      errText = `Sunucu isteği reddetti${r.httpStatus ? ` (HTTP ${r.httpStatus})` : ''}`;
      break;
    case 'istemci':
      errText = 'Tarayıcıda hata oluştu';
      break;
    default:
      errText = 'Kayıt isteği başarısız oldu';
  }
  steps.push({ time: fmtClock(occurred), dot: DOT.bad, fg: '#8a1c1c', text: `${errText}; "Ödeme Kaydı Hatası" gösterildi.` });

  switch (r.clientOutcome) {
    case 1:
      steps.push({ time: '—', dot: DOT.ok, fg: '#1f2933', text: 'Kasa yeniden denedi, satış kaydedildi.' });
      break;
    case 2:
      steps.push({ time: '—', dot: DOT.grey, fg: '#52606d', text: 'Kasiyer "Kapat"a bastı (yeniden denemedi).' });
      break;
    case 3:
      steps.push({ time: '—', dot: DOT.ok, fg: '#1f2933', text: 'Sayfa yeniden açılınca bekleyen satış kurtarıldı.' });
      break;
    default:
      steps.push({ time: '—', dot: DOT.grey, fg: '#52606d', text: 'Kasada sonrasında ne yapıldığı kaydedilmedi.' });
  }

  const checked = toDate(r.checkedAt);
  switch (r.serverCheck) {
    case 1:
      steps.push({
        time: fmtClock(checked),
        dot: DOT.ok,
        fg: '#1f2933',
        text: `Sunucu kontrolü: satış kayıtlı${r.serverReference ? ` (${r.serverReference})` : ''}.`,
      });
      break;
    case 2:
      steps.push({ time: fmtClock(checked), dot: DOT.bad, fg: '#8a1c1c', text: 'Sunucu kontrolü: bu anahtarla satış yok.' });
      break;
    case 3:
      steps.push({ time: fmtClock(checked), dot: DOT.grey, fg: '#52606d', text: 'Sunucu kontrolü yapılamadı.' });
      break;
    default:
      steps.push({ time: '—', dot: DOT.grey, fg: '#52606d', text: 'Sunucu kontrolü henüz yapılmadı.' });
  }
  return steps;
};

const attemptText = (r: PosErrorRow) => {
  const n = r.attempt ?? 1;
  if (r.clientOutcome === 1) return `${n} (yeniden denendi)`;
  if (r.clientOutcome === 2) return `${n} (yeniden denenmedi)`;
  if (r.clientOutcome === 3) return `${n} (sayfa açılınca kurtarıldı)`;
  return String(n);
};

// ---------------------------------------------------------------------------
// Sebep çubukları (alt bölüm ve "Sebeplere göre" sekmesi)
// ---------------------------------------------------------------------------
const CauseBars: React.FC<{ causes: PosErrorCause[]; loading: boolean }> = ({ causes, loading }) => {
  if (causes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {loading ? 'Yükleniyor…' : 'Bu aralıkta kasa kayıt hatası yok'}
      </Typography>
    );
  }
  const max = Math.max(...causes.map((c) => c.count), 1);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {causes.map((c) => (
        <Box
          key={c.label}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr) 48px',
            gap: 1.5,
            alignItems: 'center',
            fontSize: 14,
          }}
        >
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {c.label}
          </Typography>
          <Box sx={{ height: 10, bgcolor: '#e4e7eb', borderRadius: '5px', display: 'flex' }} aria-hidden>
            <Box sx={{ width: `${Math.round((c.count / max) * 100)}%`, bgcolor: '#1976d2', borderRadius: '5px' }} />
          </Box>
          <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 500 }}>
            {c.count.toLocaleString('tr-TR')}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const SuperAdminPosErrors: React.FC = () => {
  const [range, setRange] = useState<PosErrorRange>('today');
  const [tab, setTab] = useState<PageTab>('lost');
  const [summary, setSummary] = useState<PosErrorSummary | null>(null);
  const [rows, setRows] = useState<PosErrorRow[]>([]);
  const [rowTotal, setRowTotal] = useState(0);
  const [causes, setCauses] = useState<PosErrorCause[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [page, setPage] = useState(0);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [deviceFilter, setDeviceFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [screenFilter, setScreenFilter] = useState('');
  const [hideReviewed, setHideReviewed] = useState(false);

  const [detail, setDetail] = useState<PosErrorRow | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const loadSeq = useRef(0);
  const lastErrorRef = useRef<string | null>(null);

  useEffect(() => {
    SuperAdminService.getInstance()
      .getAllCompanies()
      .then((list) => setCompanies([...list].sort((a, b) => a.name.localeCompare(b.name, 'tr'))))
      .catch(() => setCompanies([]));
  }, []);

  // Filtre/aralık/sekme değişince ilk sayfaya dön
  useEffect(() => {
    setPage(0);
  }, [range, tab, company, deviceFilter, kindFilter, screenFilter, hideReviewed]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const listTab = tab === 'causes' ? null : tab;
      const [s, c, list] = await Promise.all([
        getPosErrorSummary(range),
        getPosErrorCauses(range),
        listTab
          ? getPosErrors({
              tab: listTab,
              range,
              companyId: company?.id,
              fiscalDevice: deviceFilter || undefined,
              errorKind: kindFilter || undefined,
              screenMode: screenFilter || undefined,
              hideReviewed,
              skip: page * PAGE_SIZE,
              take: PAGE_SIZE,
            })
          : Promise.resolve(null),
      ]);
      if (seq !== loadSeq.current) return;
      setSummary(s);
      setCauses(c.items ?? []);
      if (list) {
        setRows(list.items ?? []);
        setRowTotal(list.total ?? 0);
      }
      setError(null);
      lastErrorRef.current = null;
      setLastRefresh(new Date());
    } catch (e) {
      if (seq !== loadSeq.current) return;
      const msg = e instanceof Error ? e.message : 'Yüklenemedi';
      setError(msg);
      // Otomatik yenilemede aynı hatayı her 30 sn'de tekrar gösterme
      if (lastErrorRef.current !== msg) setToast({ msg, severity: 'error' });
      lastErrorRef.current = msg;
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [range, tab, company, deviceFilter, kindFilter, screenFilter, hideReviewed, page]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const openDetail = async (r: PosErrorRow) => {
    setDetail(r);
    setNote(r.reviewNote ?? '');
    try {
      const fresh = await getPosError(r.id);
      setDetail((cur) => (cur && cur.id === r.id ? fresh : cur));
    } catch {
      /* satırdaki veri yeterli */
    }
  };

  const doReview = async (r: PosErrorRow, reviewNote: string) => {
    setBusy(r.id);
    try {
      await reviewPosError(r.id, reviewNote.trim());
      setToast({ msg: 'İncelendi olarak işaretlendi.', severity: 'success' });
      setDetail(null);
      await load();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'İşaretlenemedi', severity: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const rangeOpt = RANGE_OPTIONS.find((o) => o.value === range) ?? RANGE_OPTIONS[0];
  const lost = summary?.lostSuspicion ?? 0;
  const num = (v?: number) => (summary && v != null ? v.toLocaleString('tr-TR') : '—');

  const stats = [
    { label: 'Kayıp şüphesi (para alındı, satış yok)', value: num(summary?.lostSuspicion), color: '#d32f2f', numColor: lost > 0 ? '#b71c1c' : '#1f2933' },
    { label: `${rangeOpt.statPrefix} görülen hata`, value: num(summary?.total), color: '#ef6c00', numColor: '#1f2933' },
    { label: 'Aslında kaydedilmiş (yanlış alarm)', value: num(summary?.savedOnServer), color: '#1976d2', numColor: '#1f2933' },
    { label: 'Yeniden denemede kaydedildi', value: num(summary?.retriedOk), color: '#2e7d32', numColor: '#1f2933' },
    { label: 'Etkilenen şirket', value: num(summary?.companies), color: '#7b1fa2', numColor: '#1f2933' },
  ];

  const tabSx = { fontWeight: 500 };

  return (
    <SuperAdminLayout title="Kasa Kayıt Hataları" icon={<PointOfSaleIcon />} maxWidth={false}>
      <Box sx={{ p: { xs: 0, md: 1 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 420px' }}>
            Kasada "Ödeme Kaydı Hatası" ekranı her çıktığında burada bir satır oluşur. Sistem, satışın sunucuda gerçekten
            kaydedilip kaydedilmediğini kendisi kontrol eder. Kırmızı satırlar: yazarkasa parayı aldı ama satış GTS'de yok.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="pe-range">Tarih</InputLabel>
            <Select
              labelId="pe-range"
              label="Tarih"
              value={range}
              onChange={(e) => setRange(e.target.value as PosErrorRange)}
            >
              {RANGE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {lastRefresh ? `Son güncelleme ${fmtClock(lastRefresh)} · 30 sn'de bir yenilenir` : ''}
          </Typography>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Yenile
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          {stats.map((s) => (
            <Grid item xs={6} sm={4} md key={s.label}>
              <StatCard elevation={1}>
                <Box sx={{ width: 12, height: 12, borderRadius: '6px', bgcolor: s.color }} aria-hidden />
                <Typography variant="h5" sx={{ fontWeight: 700, color: s.numColor }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
              </StatCard>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v: PageTab) => setTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              value="lost"
              label={`KAYIP ŞÜPHESİ${summary ? ` (${summary.lostSuspicion})` : ''}`}
              sx={{ ...tabSx, color: '#b71c1c' }}
            />
            <Tab value="all" label={`TÜM HATALAR${summary ? ` (${summary.total})` : ''}`} sx={tabSx} />
            <Tab value="causes" label="SEBEPLERE GÖRE" sx={tabSx} />
          </Tabs>

          {tab === 'causes' ? (
            <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {rangeOpt.causesTitle}
              </Typography>
              <CauseBars causes={causes} loading={loading} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, p: 2, alignItems: 'center' }}>
                <Autocomplete
                  size="small"
                  sx={{ flex: '1 1 220px', minWidth: 220 }}
                  options={companies}
                  value={company}
                  onChange={(_, v) => setCompany(v)}
                  getOptionLabel={(c) => c.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  noOptionsText="Şirket bulunamadı"
                  renderInput={(params) => <TextField {...params} label="Şirket" placeholder="Şirket adı yazın" />}
                />
                <FormControl size="small" sx={{ flex: '0 1 200px', minWidth: 170 }}>
                  <InputLabel id="pe-device">Ödeme yolu</InputLabel>
                  <Select
                    labelId="pe-device"
                    label="Ödeme yolu"
                    value={deviceFilter}
                    onChange={(e) => setDeviceFilter(e.target.value)}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    {Object.entries(FISCAL_DEVICE_LABEL).map(([k, v]) => (
                      <MenuItem key={k} value={k}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: '0 1 180px', minWidth: 160 }}>
                  <InputLabel id="pe-kind">Hata türü</InputLabel>
                  <Select labelId="pe-kind" label="Hata türü" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
                    <MenuItem value="">Tümü</MenuItem>
                    {Object.entries(ERROR_KIND_LABEL).map(([k, v]) => (
                      <MenuItem key={k} value={k}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: '0 1 160px', minWidth: 140 }}>
                  <InputLabel id="pe-screen">Ekran</InputLabel>
                  <Select
                    labelId="pe-screen"
                    label="Ekran"
                    value={screenFilter}
                    onChange={(e) => setScreenFilter(e.target.value)}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    {Object.entries(SCREEN_LABEL).map(([k, v]) => (
                      <MenuItem key={k} value={k}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Checkbox checked={hideReviewed} onChange={(e) => setHideReviewed(e.target.checked)} />}
                  label="İncelenenleri gizle"
                />
              </Box>

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1080 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { color: '#52606d', fontWeight: 500, fontSize: 13 } }}>
                      <TableCell>Sonuç</TableCell>
                      <TableCell>Zaman</TableCell>
                      <TableCell>Şirket / Şube</TableCell>
                      <TableCell>Kasiyer</TableCell>
                      <TableCell>Ödeme</TableCell>
                      <TableCell>Kasada görülen hata</TableCell>
                      <TableCell align="right">İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 && loading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                          {tab === 'lost' && (summary?.total ?? 0) > 0
                            ? 'Bu aralıkta kayıp şüphesi yok. Tüm hatalar sunucuda kontrol edildi.'
                            : 'Bu aralıkta kasa kayıt hatası yok'}
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.map((r) => {
                      const pill = pillOf(r);
                      const lostRow = r.result === 'kayip-suphesi';
                      const sub = resultNote(r);
                      const screen = screenText(r);
                      const dur = fmtDuration(r.durationMs);
                      return (
                        <TableRow
                          key={r.id}
                          hover
                          sx={{ verticalAlign: 'top', bgcolor: lostRow ? '#fff8f7' : undefined }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                bgcolor: pill.bg,
                                color: pill.fg,
                                borderRadius: '12px',
                                px: 1.25,
                                py: 0.25,
                                fontSize: 12,
                                fontWeight: 500,
                              }}
                            >
                              {pill.label}
                            </Box>
                            {sub && (
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                {sub}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {fmtStamp(toDate(r.occurredAt))}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{r.companyName || (r.companyId ? 'Bilinmeyen şirket' : '—')}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[r.branchName || (r.branchId ? 'Şube' : 'Şube yok'), screen].filter(Boolean).join(' · ')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{r.cashierName || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                              {fmtMoney(r.amount)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {paymentPath(r)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 360 }}>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#52606d' }} component="div">
                              {errorKindText(r)}
                              {dur ? ` · ${dur}` : ''}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#8a1c1c',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                              }}
                            >
                              {r.errorMessage || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Button size="small" onClick={() => openDetail(r)}>
                              Detay
                            </Button>
                            {r.reviewedAt ? (
                              <Tooltip
                                title={`${fmtStamp(toDate(r.reviewedAt))}${r.reviewNote ? ` · ${r.reviewNote}` : ''}`}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1, display: 'inline-block', verticalAlign: 'middle' }}
                                >
                                  İncelendi{r.reviewedBy ? ` · ${personName(r.reviewedBy)}` : ''}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                sx={{ ml: 0.5, borderColor: '#c3c9d1' }}
                                disabled={busy === r.id}
                                onClick={() => doReview(r, '')}
                              >
                                İncelendi
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={rowTotal}
                page={rowTotal > 0 ? Math.min(page, Math.max(0, Math.ceil(rowTotal / PAGE_SIZE) - 1)) : 0}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                labelRowsPerPage="Sayfa başına"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
                getItemAriaLabel={(type) => (type === 'next' ? 'Sonraki sayfa' : type === 'previous' ? 'Önceki sayfa' : type === 'first' ? 'İlk sayfa' : 'Son sayfa')}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            </>
          )}
        </Paper>

        {tab !== 'causes' && (
          <Paper elevation={1} sx={{ borderRadius: 2, p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 500 }}>
              {rangeOpt.causesTitle}
            </Typography>
            <CauseBars causes={causes} loading={loading} />
          </Paper>
        )}
      </Box>

      {/* Hata detayı */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail &&
          (() => {
            const pill = pillOf(detail);
            const box = serverBox(detail);
            const steps = buildSteps(detail);
            const checked = toDate(detail.checkedAt);
            const titleParts = [
              tableTitle(detail),
              fmtMoney(detail.amount),
              detail.fiscalDevice && detail.fiscalDevice !== 'yok'
                ? deviceLabel(detail.fiscalDevice)
                : paymentKindLabel(detail.paymentKind) || '',
            ].filter(Boolean);
            const subParts = [
              detail.companyName || (detail.companyId ? 'Bilinmeyen şirket' : ''),
              detail.branchName || '',
              detail.screenMode ? `${SCREEN_LABEL[detail.screenMode] ?? detail.screenMode} ekranı` : '',
              detail.cashierName ? `Kasiyer ${detail.cashierName}` : '',
            ].filter(Boolean);
            const fields: [string, React.ReactNode, boolean?][] = [
              ['Hata türü', `${errorKindText(detail)}${fmtDuration(detail.durationMs) ? ` (${fmtDuration(detail.durationMs)})` : ''}`],
              ['Kayıt isteği', <span title={detail.endpoint ?? ''}>{endpointName(detail.endpoint)}</span>],
              ['Deneme', attemptText(detail)],
              ['İşlem anahtarı', <span title={detail.idempotencyKey ?? ''}>{shortKey(detail.idempotencyKey)}</span>, true],
              ['Pavo istek no', <span title={detail.pavoRequestId ?? ''}>{shortKey(detail.pavoRequestId)}</span>, true],
              ['Tarayıcı / sürüm', <span title={detail.userAgent ?? ''}>{browserAndVersion(detail)}</span>],
            ];
            const reviewed = !!detail.reviewedAt;
            return (
              <>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1 }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 500 }}>
                      {titleParts.join(' · ')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {subParts.join(' · ')}
                    </Typography>
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      bgcolor: pill.bg,
                      color: pill.fg,
                      borderRadius: '12px',
                      px: 1.5,
                      py: 0.5,
                      fontSize: 13,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pill.label}
                  </Box>
                  <Tooltip title="Kapat">
                    <IconButton aria-label="Kapat" onClick={() => setDetail(null)}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                  {/* Sunucu kontrolü */}
                  <Box
                    sx={{
                      bgcolor: box.bg,
                      color: box.fg,
                      borderRadius: 1.5,
                      px: 1.75,
                      py: 1.5,
                      fontSize: 14,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ fontWeight: 700 }}>{box.title}</Box>
                    <Box>{box.text}</Box>
                    {checked && <Box sx={{ fontSize: 12 }}>Son kontrol {fmtStamp(checked)}</Box>}
                  </Box>

                  {/* Kasada olanlar */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                      Kasada olanlar
                    </Typography>
                    <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                      {steps.map((s, i) => (
                        <Box
                          component="li"
                          key={i}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '76px 14px minmax(0, 1fr)',
                            gap: 1.5,
                            alignItems: 'start',
                            py: 0.75,
                          }}
                        >
                          <Box component="span" sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 13, color: '#52606d' }}>
                            {s.time}
                          </Box>
                          <Box
                            component="span"
                            aria-hidden
                            sx={{ width: 10, height: 10, borderRadius: '5px', mt: '4px', bgcolor: s.dot }}
                          />
                          <Box component="span" sx={{ fontSize: 14, color: s.fg }}>
                            {s.text}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Adımlar kayıttaki alanlardan çıkarılır; saati kaydedilmeyen adımlarda — görünür. Gönderim saati =
                      hata saati − bekleme süresi.
                    </Typography>
                  </Box>

                  <Grid container spacing={2} sx={{ fontSize: 14 }}>
                    {fields.map(([label, value, mono]) => (
                      <Grid item xs={12} sm={6} md={4} key={label}>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {label}
                        </Typography>
                        <Box
                          sx={{
                            wordBreak: 'break-word',
                            ...(mono ? { fontFamily: '"Roboto Mono", monospace', fontSize: 13 } : { fontSize: 14 }),
                          }}
                        >
                          {value}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
                      Kasada görülen mesaj
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        bgcolor: '#f5f7fa',
                        border: '1px solid #e4e7eb',
                        borderRadius: 1.5,
                        px: 1.75,
                        py: 1.5,
                        fontFamily: '"Roboto Mono", monospace',
                        fontSize: 13,
                        color: '#1f2933',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {detail.errorMessage || '—'}
                    </Box>
                  </Box>

                  {reviewed && (
                    <Typography variant="body2" color="text.secondary">
                      İncelendi: {fmtStamp(toDate(detail.reviewedAt))}
                      {detail.reviewedBy ? ` · ${personName(detail.reviewedBy)}` : ''}
                    </Typography>
                  )}
                  <TextField
                    label="İnceleme notu"
                    placeholder="ör. Şube aradı, satış elle girildi"
                    multiline
                    minRows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                  <Button color="inherit" onClick={() => setDetail(null)}>
                    Kapat
                  </Button>
                  <Button variant="contained" onClick={() => doReview(detail, note)} disabled={busy === detail.id}>
                    {reviewed ? 'Notu kaydet' : 'İncelendi olarak işaretle'}
                  </Button>
                </DialogActions>
              </>
            );
          })()}
      </Dialog>

      {toast && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        </Snackbar>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminPosErrors;
